import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import MeetingAttendeesModal from "./MeetingAttendeesModal";
import "./MeetingDetailsModal.css";
import Select from "react-select";

function MeetingDetailsModal({ meetingData, onClose }) {
    const [meetings, setMeetings] = useState(meetingData || []);
    const [editingMeeting, setEditingMeeting] = useState(null);
    const [selectedMeetingId, setSelectedMeetingId] = useState(null);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [lodges, setLodges] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [attendeesModalOpen, setAttendeesModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        lodge: "",
        eventType: "",
        year: "",
        attendance: "",
        documents: "",
        search: "",
    });
    const [sortMode, setSortMode] = useState("newest");

    useEffect(() => {
        fetchLodges();
        fetchEventTypes();
        fetchMeetings();
    }, []);

    const fetchMeetings = async () => {
        try {
            const res = await axios.get("/api/meetings/context");
            setMeetings(res.data);
            setSelectedMeetingId((current) => current || res.data[0]?.id || null);
        } catch (error) {
            console.error("Error fetching meetings:", error);
        }
    };

    const fetchLodges = async () => {
        try {
            const res = await axios.get("/api/crud/lodges");
            setLodges(res.data.map((lodge) => ({ value: lodge.id, label: lodge.name })));
        } catch (error) {
            console.error("Error fetching lodges:", error);
        }
    };

    const fetchEventTypes = async () => {
        try {
            const res = await axios.get("/api/event-types-with-id");
            setEventTypes(res.data.map((eventType) => ({ value: eventType.id, label: eventType.description })));
        } catch (error) {
            console.error("Error fetching event types:", error);
        }
    };

    const formatToSAST = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return "";
        const offsetInMs = 2 * 60 * 60 * 1000;
        const sastDate = new Date(date.getTime() + offsetInMs);
        return sastDate.toISOString().split("T")[0];
    };

    const years = useMemo(() => {
        return [...new Set(meetings.map((meeting) => formatToSAST(meeting.meeting_date).slice(0, 4)).filter(Boolean))]
            .sort((a, b) => b.localeCompare(a))
            .map((year) => ({ value: year, label: year }));
    }, [meetings]);

    const stats = useMemo(() => {
        const currentYear = new Date().getFullYear().toString();
        return {
            total: meetings.length,
            thisYear: meetings.filter((meeting) => formatToSAST(meeting.meeting_date).startsWith(currentYear)).length,
            missingAttendance: meetings.filter((meeting) => Number(meeting.attendee_count || 0) === 0).length,
            withDocuments: meetings.filter((meeting) => Number(meeting.linked_document_count || 0) > 0).length,
        };
    }, [meetings]);

    const filteredMeetings = useMemo(() => {
        const search = filters.search.trim().toLowerCase();
        const filtered = meetings.filter((meeting) => {
            const meetingYear = formatToSAST(meeting.meeting_date).slice(0, 4);
            const haystack = [
                meeting.lodge_name,
                meeting.event_type_description,
                meeting.description,
                formatToSAST(meeting.meeting_date),
                ...(meeting.document_ids || []),
            ].join(" ").toLowerCase();

            if (filters.lodge && meeting.lodge_id !== filters.lodge) return false;
            if (filters.eventType && meeting.eventtype_id !== filters.eventType) return false;
            if (filters.year && meetingYear !== filters.year) return false;
            if (filters.attendance === "missing" && Number(meeting.attendee_count || 0) > 0) return false;
            if (filters.attendance === "has" && Number(meeting.attendee_count || 0) === 0) return false;
            if (filters.documents === "missing" && Number(meeting.linked_document_count || 0) > 0) return false;
            if (filters.documents === "has" && Number(meeting.linked_document_count || 0) === 0) return false;
            if (search && !haystack.includes(search)) return false;
            return true;
        });

        return [...filtered].sort((a, b) => {
            if (sortMode === "oldest") return new Date(a.meeting_date || 0) - new Date(b.meeting_date || 0);
            if (sortMode === "lodge") return (a.lodge_name || "").localeCompare(b.lodge_name || "");
            if (sortMode === "attendance") return Number(b.attendee_count || 0) - Number(a.attendee_count || 0);
            return new Date(b.meeting_date || 0) - new Date(a.meeting_date || 0);
        });
    }, [filters, meetings, sortMode]);

    const selectedMeeting = useMemo(() => {
        return meetings.find((meeting) => meeting.id === selectedMeetingId) || filteredMeetings[0] || null;
    }, [filteredMeetings, meetings, selectedMeetingId]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({ lodge: "", eventType: "", year: "", attendance: "", documents: "", search: "" });
        setSortMode("newest");
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const validateMeeting = () => {
        const newErrors = {};
        if (!formData.lodge_id) newErrors.lodge_id = "Lodge is required";
        if (!formData.eventtype_id) newErrors.eventtype_id = "Event type is required";
        if (!formData.meeting_date) newErrors.meeting_date = "Meeting date is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateMeeting()) return;

        const documentIdsArray = formData.document_ids && formData.document_ids.trim() !== ""
            ? formData.document_ids.split(",").map((id) => id.trim()).filter(Boolean)
            : [];

        const payload = { ...formData, document_ids: documentIdsArray };

        try {
            if (editingMeeting) {
                await axios.put(`/api/meetings/${editingMeeting.id}`, payload);
                setSelectedMeetingId(editingMeeting.id);
            } else {
                const res = await axios.post("/api/meetings", payload);
                setSelectedMeetingId(res.data.id);
            }
            setEditingMeeting(null);
            setFormData({});
            await fetchMeetings();
        } catch (error) {
            console.error("Error saving meeting:", error);
            alert("Failed to save meeting.");
        }
    };

    const handleEdit = (meeting) => {
        setEditingMeeting(meeting);
        setSelectedMeetingId(meeting.id);
        setFormData({
            lodge_id: meeting.lodge_id || "",
            eventtype_id: meeting.eventtype_id || "",
            meeting_date: formatToSAST(meeting.meeting_date),
            description: meeting.description || "",
            document_ids: meeting.document_ids ? meeting.document_ids.join(", ") : "",
        });
    };

    const startNewMeeting = () => {
        setEditingMeeting(null);
        setFormData({
            lodge_id: filters.lodge || "",
            eventtype_id: "",
            meeting_date: "",
            description: "",
            document_ids: "",
        });
        setErrors({});
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this meeting?")) return;

        try {
            await axios.delete(`/api/meetings/${id}`);
            if (selectedMeetingId === id) setSelectedMeetingId(null);
            await fetchMeetings();
        } catch (error) {
            console.error("Error deleting meeting:", error);
            alert("Failed to delete meeting.");
        }
    };

    const openAttendees = (meetingId) => {
        setSelectedMeetingId(meetingId);
        setAttendeesModalOpen(true);
    };

    const closeAttendees = async () => {
        setAttendeesModalOpen(false);
        await fetchMeetings();
    };

    return (
        <div className="md-modal-overlay">
            <div className="md-modal-content">
                <div className="md-modal-header">
                    <div>
                        <p className="md-eyebrow">Meeting Register</p>
                        <h2>Meetings</h2>
                    </div>
                    <button className="md-modal-close-btn" onClick={onClose}>&times;</button>
                </div>

                <div className="md-summary-grid">
                    <div><strong>{stats.total}</strong><span>Total meetings</span></div>
                    <div><strong>{stats.thisYear}</strong><span>This year</span></div>
                    <div><strong>{stats.missingAttendance}</strong><span>Need attendees</span></div>
                    <div><strong>{stats.withDocuments}</strong><span>With documents</span></div>
                </div>

                <div className="md-workspace">
                    <aside className="md-filter-panel">
                        <h3>Find Meetings</h3>
                        <label>Search</label>
                        <input
                            type="search"
                            value={filters.search}
                            onChange={(e) => handleFilterChange("search", e.target.value)}
                            placeholder="Search lodge, date, type, notes..."
                        />

                        <label>Lodge</label>
                        <Select
                            options={[{ value: "", label: "All Lodges" }, ...lodges]}
                            value={lodges.find((lodge) => lodge.value === filters.lodge) || { value: "", label: "All Lodges" }}
                            onChange={(option) => handleFilterChange("lodge", option?.value || "")}
                            isClearable
                            isSearchable
                        />

                        <label>Event Type</label>
                        <Select
                            options={[{ value: "", label: "All Event Types" }, ...eventTypes]}
                            value={eventTypes.find((event) => event.value === filters.eventType) || { value: "", label: "All Event Types" }}
                            onChange={(option) => handleFilterChange("eventType", option?.value || "")}
                            isClearable
                            isSearchable
                        />

                        <label>Year</label>
                        <Select
                            options={[{ value: "", label: "All Years" }, ...years]}
                            value={years.find((year) => year.value === filters.year) || { value: "", label: "All Years" }}
                            onChange={(option) => handleFilterChange("year", option?.value || "")}
                            isClearable
                            isSearchable
                        />

                        <label>Attendance</label>
                        <select value={filters.attendance} onChange={(e) => handleFilterChange("attendance", e.target.value)}>
                            <option value="">Any attendance</option>
                            <option value="has">Has attendees</option>
                            <option value="missing">Missing attendees</option>
                        </select>

                        <label>Documents</label>
                        <select value={filters.documents} onChange={(e) => handleFilterChange("documents", e.target.value)}>
                            <option value="">Any documents</option>
                            <option value="has">Has linked documents</option>
                            <option value="missing">Missing documents</option>
                        </select>

                        <label>Sort</label>
                        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="lodge">Lodge A-Z</option>
                            <option value="attendance">Attendance high-low</option>
                        </select>

                        <button type="button" className="md-secondary-btn" onClick={clearFilters}>Clear Filters</button>
                        <button type="button" className="md-primary-btn" onClick={startNewMeeting}>Add Meeting</button>
                    </aside>

                    <section className="md-list-panel">
                        <div className="md-list-heading">
                            <h3>{filteredMeetings.length} matching meetings</h3>
                            <span>{meetings.length} in register</span>
                        </div>
                        <div className="md-meeting-list">
                            {filteredMeetings.map((meeting) => {
                                const selected = selectedMeeting?.id === meeting.id;
                                return (
                                    <button
                                        key={meeting.id}
                                        type="button"
                                        className={`md-meeting-card ${selected ? "active" : ""}`}
                                        onClick={() => setSelectedMeetingId(meeting.id)}
                                    >
                                        <span className="md-date-pill">{formatToSAST(meeting.meeting_date) || "No date"}</span>
                                        <strong>{meeting.lodge_name || "Unknown Lodge"}</strong>
                                        <span>{meeting.event_type_description || "Unknown Type"}</span>
                                        {meeting.description && <p>{meeting.description}</p>}
                                        <div className="md-card-badges">
                                            <span>{meeting.attendee_count || 0} attendees</span>
                                            <span>{meeting.visitor_attendee_count || 0} visitors</span>
                                            <span>{meeting.linked_document_count || 0} docs</span>
                                        </div>
                                    </button>
                                );
                            })}
                            {filteredMeetings.length === 0 && <p className="md-empty-note">No meetings match these filters.</p>}
                        </div>
                    </section>

                    <section className="md-detail-panel">
                        <div className="md-detail-header">
                            <div>
                                <p className="md-eyebrow">Selected Meeting</p>
                                <h3>{selectedMeeting?.lodge_name || "No meeting selected"}</h3>
                            </div>
                            {selectedMeeting && <span>{formatToSAST(selectedMeeting.meeting_date)}</span>}
                        </div>

                        {selectedMeeting && (
                            <div className="md-selected-summary">
                                <div><strong>{selectedMeeting.event_type_description || "Meeting"}</strong><span>Event type</span></div>
                                <div><strong>{selectedMeeting.attendee_count || 0}</strong><span>Attendees</span></div>
                                <div><strong>{selectedMeeting.visitor_attendee_count || 0}</strong><span>Visitors</span></div>
                                <div><strong>{selectedMeeting.linked_document_count || 0}</strong><span>Documents</span></div>
                            </div>
                        )}

                        {selectedMeeting?.description && <p className="md-selected-description">{selectedMeeting.description}</p>}

                        <div className="md-detail-actions">
                            <button type="button" onClick={() => selectedMeeting && handleEdit(selectedMeeting)} disabled={!selectedMeeting}>Edit</button>
                            <button type="button" onClick={() => selectedMeeting && openAttendees(selectedMeeting.id)} disabled={!selectedMeeting}>Manage Attendees</button>
                            <button type="button" className="danger" onClick={() => selectedMeeting && handleDelete(selectedMeeting.id)} disabled={!selectedMeeting}>Delete</button>
                        </div>

                        <form className="md-meeting-form">
                            <h3>{editingMeeting ? "Edit Meeting" : "Add / Update Meeting"}</h3>

                            <label>Lodge</label>
                            <Select
                                name="lodge_id"
                                options={lodges}
                                value={lodges.find((lodge) => lodge.value === formData.lodge_id) || null}
                                onChange={(option) => setFormData((prev) => ({ ...prev, lodge_id: option?.value || "" }))}
                                placeholder="Select Lodge"
                                className={errors.lodge_id ? "md-error" : ""}
                                isSearchable
                            />
                            {errors.lodge_id && <p className="md-error-message">{errors.lodge_id}</p>}

                            <label>Event Type</label>
                            <Select
                                name="eventtype_id"
                                options={eventTypes}
                                value={eventTypes.find((event) => event.value === formData.eventtype_id) || null}
                                onChange={(option) => setFormData((prev) => ({ ...prev, eventtype_id: option?.value || "" }))}
                                placeholder="Select Event Type"
                                className={errors.eventtype_id ? "md-error" : ""}
                                isSearchable
                            />
                            {errors.eventtype_id && <p className="md-error-message">{errors.eventtype_id}</p>}

                            <label>Meeting Date</label>
                            <input type="date" name="meeting_date" value={formData.meeting_date || ""} onChange={handleInputChange} className={errors.meeting_date ? "md-error" : ""} />
                            {errors.meeting_date && <p className="md-error-message">{errors.meeting_date}</p>}

                            <label>Description</label>
                            <textarea name="description" value={formData.description || ""} onChange={handleInputChange} rows="4" />

                            <label>Document IDs</label>
                            <input type="text" name="document_ids" value={formData.document_ids || ""} onChange={handleInputChange} placeholder="uuid1, uuid2, uuid3" />

                            <div className="md-form-actions">
                                <button type="button" className="md-meeting-save-btn" onClick={handleSave}>{editingMeeting ? "Update Meeting" : "Save Meeting"}</button>
                                <button type="button" className="md-secondary-btn" onClick={startNewMeeting}>New</button>
                            </div>
                        </form>
                    </section>
                </div>
            </div>

            {attendeesModalOpen && selectedMeetingId && (
                <MeetingAttendeesModal meetingId={selectedMeetingId} meeting={selectedMeeting} onClose={closeAttendees} />
            )}
        </div>
    );
}

export default MeetingDetailsModal;
